import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Location, MenuTypesWithPoduct } from "@/types/pos";
import { ArrowRight, Backpack, ChevronLeft, Loader2, X } from "lucide-react";
import bgSparkleOne from '@/assets/pos/bg-sparkle-1.svg';
import bgSparkleTwo from '@/assets/pos/bg-sparkle-2.svg';
import bgSparkleThree from '@/assets/pos/bg-sparkle-3.svg';
import { useQuery, useQueries } from "@tanstack/react-query";
import { usePosOrders } from "@/hooks/pos/use-order";
import OrderStatusBanner from "./order-status-banner";
import { isMenuAvailable } from "@/lib/pos/global";
import { apiFetch } from "@/utils";

interface LocationSelectionProps {
	onLocationSelect: (location: { id: number; name?: string; details?: string, menuTypes?: MenuTypesWithPoduct[]; isLoading?: boolean }) => void;
}

const fetchLocations = async (active?: boolean): Promise<Location[]> => {
	const response = await apiFetch(`/v1/pos/locations?active=${active}`, { withCredentials: true });
	if (!response.ok) throw new Error("Failed to fetch locations");
	return response.json();
};

const fetchMenuTypes = async (locationId: number) => {
  const res = await apiFetch(`/v1/pos/locations/${locationId}/menu-types`, { withCredentials: true });
  if (!res.ok) throw new Error("Failed to fetch menus");
  return res.json();
};


export default function LocationSelection({ onLocationSelect }: LocationSelectionProps) {
	const [, navigate] = useLocation();
	const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
	const [roomNumber, setRoomNumber] = useState("");
	const [isMenuTypeLoading, setIsMenuTypeLoading] = useState(false);
	const { orders } = usePosOrders();

	const { data: locations = [], isLoading: isLoading, isError } = useQuery({
		queryKey: ["locations", true],
		queryFn: () => fetchLocations(true),
		retry: 3,
		retryDelay: 1000,
	});

	// Fetch menu types for all locations to check availability
	const menuQueries = useQueries({
		queries: locations.map((loc: Location) => ({
			queryKey: ["location-menus", loc.id],
			queryFn: () => fetchMenuTypes(loc.id),
			enabled: !!loc.id,
			staleTime: 60000, // Cache for 1 minute
		})),
	});

	// Check if a location has any available menu items
	const isLocationOpen = (locationId: number): boolean => {
		const locationIndex = locations.findIndex((loc: Location) => loc.id === locationId);
		if (locationIndex === -1) return true; // Default to open if not found

		const menuQuery = menuQueries[locationIndex];
		if (!menuQuery?.data) return true; // Default to open while loading

		const menuTypes = menuQuery.data as MenuTypesWithPoduct[];
		// Check if any menu type is available and has items
		return menuTypes.some(mt => isMenuAvailable(mt) && mt.items.length > 0);
	};

	const locationColors = useMemo(() => {
		const obj: Record<string, string> = {};
		locations?.forEach((loc: Location) => {
			obj[loc.id] = `pos-loc-bg-${Math.floor(Math.random() * 6) + 1}`;
		});
		return obj;
	}, [locations]);

	const handleLocationApi = async (locationId: number, name: string, extraDetails?: string) => {
		try {
			setIsMenuTypeLoading(true);
			const menuTypes = await fetchMenuTypes(locationId);
			onLocationSelect({
				id: locationId,
				name,
				details: extraDetails,
				menuTypes,
				isLoading: isMenuTypeLoading
			});

		} catch (error) {
			console.error("Failed to fetch menu types", error);
		} finally {
			setIsMenuTypeLoading(false);
		}
	};

	const handleLocationClick = (location: Location) => {
		if (location?.label) {
			setSelectedLocation(location);
			return;
		}
		handleLocationApi(location.id, location.name);
	};

	const handleRoomServiceSubmit = async () => {
		if (!roomNumber.trim()) return;

		if (!selectedLocation) return;

		await handleLocationApi(
			selectedLocation.id,
			selectedLocation.name,
			roomNumber.trim()
		);
	};

	return (
		<div className="min-h-auto bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				{/* Header with back button */}
				<div
					role="banner"
					className="sticky top-0 z-10 flex items-center justify-between py-3 bg-[#18181B] backdrop-blur-md"
				>
					<div className="flex items-center gap-3">
						<button
							type="button"
							aria-label="Go back"
							onClick={() => navigate('/')}
							className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
						>
							<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
						</button>
						<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide text-[var(--pos-primary-text-color)]">
							Order
						</h1>
					</div>
					<button
						type="button"
						onClick={() => navigate('/order/history')}
						className="flex items-center gap-1 cursor-pointer"
					>
						<span className="text-[12px] leading-[18px] font-figtree font-medium text-[var(--pos-primary-text-color)]">Order History</span>
						<div className="rounded-full bg-[var(--pos-secondary-background-color)] h-[28px] w-[28px] flex items-center justify-center">
							<Backpack className="text-[var(--pos-primary-text-color)] w-5 h-5" />
						</div>
					</button>
				</div>

				{/* Location Options */}
				{isLoading && (
					<div className="flex items-center justify-center py-20">
						<Loader2 className="h-6 w-6 animate-spin text-[var(--pos-secondary-text-color)]"/>
					</div>
				)}

				{isError && (
					<p className="text-center text-red-500">
						Failed to load locations
					</p>
				)}

				<p className="text-xl leading-[22px] font-figtree font-bold text-[var(--pos-primary-text-color)] mb-5">Select your location to get started</p>

				<div className="flex flex-wrap gap-2 pb-[129px]">
					{locations?.filter((loc: Location) => loc.active)?.map((loc: Location) => {
						const isOpen = isLocationOpen(loc.id);
						return (
							<button
								key={loc?.id}
								onClick={() => isOpen && handleLocationClick(loc)}
								disabled={!isOpen}
								className={`w-full sm:w-[calc(50%-4px)] overflow-hidden rounded-xl border-0 p-3 h-[138px] transition-all duration-300 transform ${
									isOpen
										? `hover:scale-[1.02] active:scale-[0.98] ${locationColors[loc.id]}`
										: "grayscale opacity-60 cursor-not-allowed bg-[var(--pos-disabled-background-color)]"
								}`}
							>
								<div className="h-full w-full relative">
									{isOpen && (
										<>
											<img className="absolute bottom-[-15px] right-[-12px]" src={bgSparkleOne} alt="" />
											<img className="absolute top-[-30px] right-[-12px]" src={bgSparkleTwo} alt="" />
											<img className="absolute bottom-[-16px] left-[-16px]" src={bgSparkleThree} alt="" />
										</>
									)}
									<div className="h-full flex flex-col justify-center">
										<h3 className="font-domine w-[90px] mb-1 text-[#ffffff] text-start text-[28px] leading-8 font-bold">{loc?.name}</h3>
										{isOpen && (
											<p className="text-[#ffffff] font-normal text-[14px] leading-[18px] font-figtree text-start">
												{loc?.description}
											</p>
										)}
									</div>
									{isOpen ? (
										<div className="h-7 w-7 bg-[#ffffff] rounded-full flex items-center justify-center absolute bottom-0 right-0">
											<ArrowRight className="w-[18px] h-[18px] flex items-center justify-center text-[#676879]"/>
										</div>
									) : (
										<div className="absolute bottom-0 right-0 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-full">
											CLOSED
										</div>
									)}
								</div>
							</button>
						);
					})}
				</div>

				<OrderStatusBanner orders={orders} onOpenHistory={() => navigate('/order/history')} />
			</div>

			{/* Room Number Input */}
			{selectedLocation?.label && (
				<>
					{/* Overlay */}
					<div className="fixed inset-0 backdrop-blur-[6px] bg-black/80 z-40" onClick={() => setSelectedLocation(null)} />

					{/* Bottom Sheet */}
					<div className="fixed bottom-0 left-0 right-0 z-[51] animate-slideUp">
						<div className="bg-[#2F2F32] rounded-t-[36px] p-6 shadow-2xl pb-[60px]">
							<div className="flex items-center justify-start mb-8">
								<h2 className="font-figtree font-semibold text-[20px] leading-[22px] text-center w-full text-white">
									Your {selectedLocation.label}
								</h2>
								<button
									onClick={() => setSelectedLocation(null)}
									className="h-5 w-5 flex items-center justify-center"
								>
									<X className="font-figtree font-semibold text-[#ffffff]"></X>
								</button>
							</div>

							{/* Input */}
							<Label htmlFor="roomNumber" className="font-figtree font-medium text-[13px] leading-[16px] text-[#F3F3F3]">
								{selectedLocation.label}
							</Label>
							<Input
								id="roomNumber"
								value={roomNumber}
								onChange={(e) => setRoomNumber(e.target.value)}
								placeholder={`Enter your ${selectedLocation?.label}`}
								className="mt-2 h-12 font-figtree text-[12px] rounded-xl bg-[var(--pos-input-background-color)] text-[var(--pos-primary-text-color)] border-[var(--pos-input-border-color)] focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0 ring-0 ring-offset-0 outline-none"
								autoFocus
							/>

							{/* CTA */}
							<Button
								onClick={handleRoomServiceSubmit}
								disabled={!roomNumber.trim()}
								className="font-figtree font-semibold w-full h-12 mt-6 text-[14px] rounded-[24px] bg-red-600"
							>Continue</Button>
						</div>
					</div>
				</>
			)}

		</div>
	);
}
